import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type AIAssistantStatus =
  | 'sending'
  | 'thinking'
  | 'searching_web'
  | 'generating_response'
  | 'using_tools'
  | 'get_client_context'
  | 'get_company_context'
  | 'get_workspace_context'
  | 'search'
  | 'taking_longer'
  | 'error'
  | 'canceling'
  | 'canceled'
  | 'blocked';

export interface AIAssistantStatusState {
  statuses: Record<
    string,
    {
      status: AIAssistantStatus;
      metadata?: {
        tool?: string;
        query?: string;
        errorMessage?: string;
        messageId?: string;
      };
      messageId?: string;
    }
  >;
}

const initialState: AIAssistantStatusState = {
  statuses: {},
};

interface SetStatusPayload {
  channelId: string;
  status: AIAssistantStatus | ''; // Allow empty string for clearing
  metadata?: {
    tool?: string;
    query?: string;
    errorMessage?: string;
    messageId?: string;
  };
  messageId?: string;
}

interface ClearStatusPayload {
  channelId: string;
}

const aiAssistantStatusSlice = createSlice({
  name: 'aiAssistantStatus',
  initialState,
  reducers: {
    setStatus: (state, action: PayloadAction<SetStatusPayload>) => {
      const { channelId, status, metadata, messageId } = action.payload;
      const existing = state.statuses[channelId];

      // If trying to clear status (empty string) but we have an active status,
      // ignore the clear - message is still in-flight
      if (!status && existing) {
        return; // Don't clear - message still processing
      }

      // Only update if we have a valid status (not empty string)
      if (status) {
        // Update logic:
        // 1. Always update if no existing status
        // 2. Allow "claiming" - backend status with messageId can claim optimistic status without messageId
        // 3. Allow updates when message IDs match (for status transitions like canceling/canceled)
        // 4. Allow optimistic updates only if there's no existing status with a message ID
        const shouldUpdate =
          !existing ||
          (messageId && !existing.messageId) || // Claiming optimistic status
          (messageId && existing.messageId === messageId) || // Same message ID - status transition
          (!messageId && !existing.messageId); // Both optimistic

        if (shouldUpdate) {
          state.statuses[channelId] = {
            status: status as AIAssistantStatus,
            metadata,
            messageId,
          };
        }
      }
    },
    clearStatus: (state, action: PayloadAction<ClearStatusPayload>) => {
      const { channelId } = action.payload;
      delete state.statuses[channelId];
    },
  },
});

export const { setStatus, clearStatus } = aiAssistantStatusSlice.actions;

export default aiAssistantStatusSlice.reducer;
